import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import {
  Box,
  CircularProgress,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SaveSharpIcon from '@mui/icons-material/SaveSharp';
import ClearSharpIcon from '@mui/icons-material/ClearSharp';

import { client } from 'src/utils/api-client';
import ConfirmationModal from 'src/components/ConfirmationModal';
import { StyledFlex } from 'src/components/StyledFlex';
import { StyledPanel, StyledTitle } from 'src/Superadmin/index.styles';
import PageLoading from 'src/components/PageLoading';
import SomethingWentWrong from 'src/components/SomethingWentWrong';
import CountryPicker, {
  CountryPickerOption,
} from 'src/components/CountryPicker';
import { useBranchesWithCountry } from 'src/utils/branches';
import { useAppSelector } from 'src/redux-file/hooks';
import CountryGroupHeader, {
  CountryGroupBulkControls,
} from 'src/Superadmin/components/CountryGroupHeader';
import {
  countryKeysOf,
  useCountryGroupCollapse,
} from 'src/Superadmin/components/CountryGroupHeader/useCountryGroupCollapse';
import { ContentProps } from '../index.types';

// R-20 (specs/001-raben-country-masteradmin/mass-specs/specs/
// 20260826-settings-commitment-windows.md): third Settings tab, alongside
// Structure and Users, letting the client manage C15's CommitmentWindow rows
// themselves instead of emailing us for a Django admin edit. Goal 3 reuses
// the SAME country grouping/folding/filter those two tabs already use
// (C09/C12/fb029efc2) -- this file intentionally mirrors their pattern
// rather than introducing a new one, per this feature's own guardrail 1 (no
// UI redesign).
//
// Everything for this tab lives in this one file (no Row.tsx/Table.tsx
// split like Structure/Users have) -- this spec's own Affected Files list
// names only this index.tsx, and the tab's shape (one flat list of
// branches, not a wide multi-column tree) doesn't need the extra files
// those two tabs split into.

const WINDOWS_URL = 'web/settings_v2/commitment-windows';

// Mirrors StructureTable.tsx's own NO_COUNTRY_VALUE convention for the same
// concept ("Bez kraju" branches, C04 rollout bucket) -- used here purely as
// a CLIENT-side grouping/filter key since this endpoint has no query params
// of its own (Interface/Contract: GET takes no filters at all, the full
// caller-scoped list comes back in one response).
const NO_COUNTRY_VALUE = 'none';

type CommitmentWindowState = 'active' | 'upcoming' | 'finished' | 'off';

export interface CommitmentWindowRowData {
  branch: string;
  branch_description: string;
  country_code: string | null;
  starts_on: string | null;
  ends_on: string | null;
  active: boolean;
  state: CommitmentWindowState;
  // Additive beyond the spec's literal Interface/Contract table -- see the
  // matching comment on CommitmentWindowOutSchema in settings_panel/
  // api_v2.py. BR-2: drives which rows render editable controls at all.
  can_edit: boolean;
}

interface CommitmentWindowGroup {
  key: string;
  label: string;
  rows: CommitmentWindowRowData[];
}

// Groups the (already caller-scoped, per BR-1) rows this screen already has
// by country -- first-seen order, "Bez kraju" trailing, same shape as
// StructureTable.tsx's/Row.tsx's own groupBranchesByCountry helpers.
// Deliberately built from THIS screen's own rows rather than the global
// useBranchesWithCountry() list every sibling tab uses for the same job:
// this endpoint's rows are already narrowed to the caller's own scope, so
// grouping/filtering from them can never offer a country the caller cannot
// manage (a country-scoped admin's picker only ever lists countries they
// actually have branches in).
function groupRowsByCountry(
  rows: CommitmentWindowRowData[],
  countryNameByCode: Map<string, string>,
  noCountryLabel: string
): CommitmentWindowGroup[] {
  const groups: CommitmentWindowGroup[] = [];
  const indexByCode = new Map<string, number>();
  let noCountryGroup: CommitmentWindowGroup | null = null;

  rows.forEach((row) => {
    if (!row.country_code) {
      if (!noCountryGroup) {
        noCountryGroup = {
          key: NO_COUNTRY_VALUE,
          label: noCountryLabel,
          rows: [],
        };
      }
      noCountryGroup.rows.push(row);
      return;
    }

    let index = indexByCode.get(row.country_code);
    if (index === undefined) {
      index = groups.length;
      indexByCode.set(row.country_code, index);
      groups.push({
        key: row.country_code,
        label: countryNameByCode.get(row.country_code) || row.country_code,
        rows: [],
      });
    }
    groups[index].rows.push(row);
  });

  if (noCountryGroup) groups.push(noCountryGroup);
  return groups;
}

function useCommitmentWindowMutations() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const save = useMutation({
    mutationFn: ({
      branch,
      starts_on,
      ends_on,
      active,
    }: {
      branch: string;
      starts_on: string;
      ends_on: string;
      active: boolean;
    }) =>
      client.put(`${WINDOWS_URL}/${branch}`, { starts_on, ends_on, active }),
    onSuccess: () => {
      queryClient.invalidateQueries('commitment-windows');
      toast.success(
        t('superadmin.settings.tabs.commitment-windows.toasts.saved') as string
      );
    },
  });

  // Client requests 2026-08-27 (country switch -> country dates -> "powinno
  // lepiej działać"): every country-level action fans out into one PUT per
  // branch (there is no bulk endpoint and this stays within the per-branch
  // contract), each with ITS OWN payload -- turning a country off keeps
  // every branch's dates while date-propagation keeps every branch's
  // active flag -- with a single refetch + toast at the end.
  const bulkSave = useMutation({
    mutationFn: async (
      items: Array<{
        branch: string;
        starts_on: string;
        ends_on: string;
        active: boolean;
      }>
    ) => {
      for (const item of items) {
        // eslint-disable-next-line no-await-in-loop
        await client.put(`${WINDOWS_URL}/${item.branch}`, {
          starts_on: item.starts_on,
          ends_on: item.ends_on,
          active: item.active,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries('commitment-windows');
      toast.success(
        t('superadmin.settings.tabs.commitment-windows.toasts.saved') as string
      );
    },
  });

  const clearWindow = useMutation({
    mutationFn: (branch: string) => client.delete(`${WINDOWS_URL}/${branch}`),
    onSuccess: () => {
      queryClient.invalidateQueries('commitment-windows');
      toast.success(
        t(
          'superadmin.settings.tabs.commitment-windows.toasts.cleared'
        ) as string
      );
    },
  });

  return { save, clearWindow, bulkSave };
}

// Same visual half-way treatment CompanyFilter's country switches use for a
// partial selection (see its PARTIAL_SWITCH_SX comment): thumb roughly
// mid-track, lighter track -- cosmetic only, `checked` keeps its plain
// "every dated window active" meaning.
const PARTIAL_SWITCH_SX = {
  '& .MuiSwitch-switchBase': {
    transform: 'translateX(8px) !important',
  },
  '& .MuiSwitch-track': {
    opacity: '0.75 !important',
  },
};

// The value every editable row in a group agrees on, or '' when they
// differ -- prefills the country row's date fields so a uniform group
// (the normal case: one window per country) shows its window up top even
// while collapsed, and a mixed group starts blank instead of lying.
function commonRowValue(
  rows: CommitmentWindowRowData[],
  pick: (_row: CommitmentWindowRowData) => string | null
): string {
  if (rows.length === 0) return '';
  const first = pick(rows[0]) || '';
  return rows.every((row) => (pick(row) || '') === first) ? first : '';
}

interface GroupRowProps {
  group: CommitmentWindowGroup;
  isOpen: boolean;
  onToggleFold: () => void;
  foldActionLabel: string;
  isSaving: boolean;
  // The save icon: apply THESE dates to every editable branch, keeping
  // each branch's own active flag.
  onSaveDates: (_values: { starts_on: string; ends_on: string }) => void;
  // The switch: immediately (de)activate the whole country. ON carries the
  // country row's dates to every branch; OFF keeps each branch's own dates.
  onBulkActive: (
    _active: boolean,
    _dates: { starts_on: string; ends_on: string }
  ) => void;
}

// The country header row is the whole-country lever (client requests
// 2026-08-27, refined the same day: "jak odpalam to wszystkie się
// zaznaczają i aktualizują datę ... jak wyłączam to wszystkie wyłącza").
// The switch acts IMMEDIATELY: turning it on first asks (a mini confirm)
// whether the country row's dates should apply to every branch, then
// activates them all -- so individual branches can be re-dated afterwards
// without flipping them one by one; turning it off deactivates the whole
// country while keeping every branch's dates. The save icon propagates
// dates alone, never touching activation. Read-only callers get the plain
// fold header and nothing else (BR-2).
function CommitmentWindowGroupRow(props: GroupRowProps) {
  const {
    group,
    isOpen,
    onToggleFold,
    foldActionLabel,
    isSaving,
    onSaveDates,
    onBulkActive,
  } = props;
  const { t } = useTranslation();

  const editableRows = React.useMemo(
    () => group.rows.filter((row) => row.can_edit),
    [group.rows]
  );
  const commonStart = commonRowValue(editableRows, (row) => row.starts_on);
  const commonEnd = commonRowValue(editableRows, (row) => row.ends_on);
  const datedRows = editableRows.filter((row) => row.starts_on && row.ends_on);
  const activeCount = datedRows.filter((row) => row.active).length;
  const serverAllActive =
    datedRows.length > 0 && activeCount === datedRows.length;
  const serverPartial = activeCount > 0 && !serverAllActive;

  const [startsOn, setStartsOn] = useState(commonStart);
  const [endsOn, setEndsOn] = useState(commonEnd);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Re-synced from the server rows after every refetch, same convention as
  // CommitmentWindowRow's own buffer.
  useEffect(() => {
    setStartsOn(commonStart);
    setEndsOn(commonEnd);
  }, [commonStart, commonEnd]);

  const showForm = editableRows.length > 0;
  const hasDates = Boolean(startsOn) && Boolean(endsOn);
  const canSave = hasDates && !isSaving;
  // Turning ON needs the country dates (they're what every branch will
  // get); turning OFF only needs something to deactivate.
  const switchDisabled = isSaving || (!serverAllActive && !hasDates);

  const handleSwitch = (checked: boolean) => {
    if (checked) {
      setConfirmOpen(true);
    } else {
      onBulkActive(false, { starts_on: startsOn, ends_on: endsOn });
    }
  };

  const confirmActivate = () => {
    setConfirmOpen(false);
    onBulkActive(true, { starts_on: startsOn, ends_on: endsOn });
  };

  return (
    <TableRow>
      <TableCell data-testid="country-group-header" sx={{ p: 0 }}>
        <CountryGroupHeader
          label={group.label}
          isOpen={isOpen}
          onToggle={onToggleFold}
          actionLabel={foldActionLabel}
        />
      </TableCell>
      {!showForm ? (
        <TableCell colSpan={5} sx={{ p: 0 }} />
      ) : (
        <>
          <TableCell>
            <TextField
              type="date"
              size="small"
              value={startsOn}
              onChange={(e) => setStartsOn(e.target.value)}
              inputProps={{
                'aria-label': `${t(
                  'superadmin.settings.tabs.commitment-windows.table.startsOn'
                )} - ${group.label}`,
              }}
            />
          </TableCell>
          <TableCell>
            <TextField
              type="date"
              size="small"
              value={endsOn}
              onChange={(e) => setEndsOn(e.target.value)}
              inputProps={{
                'aria-label': `${t(
                  'superadmin.settings.tabs.commitment-windows.table.endsOn'
                )} - ${group.label}`,
              }}
            />
          </TableCell>
          <TableCell align="center">
            <Switch
              checked={serverAllActive}
              disabled={switchDisabled}
              onChange={(e) => handleSwitch(e.target.checked)}
              aria-label={`${t(
                'superadmin.settings.tabs.commitment-windows.table.active'
              )} - ${group.label}`}
              data-partial={serverPartial || undefined}
              sx={serverPartial ? PARTIAL_SWITCH_SX : undefined}
            />
          </TableCell>
          <TableCell />
          <TableCell align="center">
            <Tooltip
              title={String(
                t('superadmin.settings.tabs.commitment-windows.buttons.save')
              )}
            >
              <span>
                <IconButton
                  size="small"
                  disabled={!canSave}
                  aria-label={`${t(
                    'superadmin.settings.tabs.commitment-windows.buttons.save'
                  )} - ${group.label}`}
                  onClick={() =>
                    onSaveDates({ starts_on: startsOn, ends_on: endsOn })
                  }
                >
                  {isSaving ? (
                    <CircularProgress size={16} />
                  ) : (
                    <SaveSharpIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </TableCell>
          <ConfirmationModal
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            onFalse={() => setConfirmOpen(false)}
            onTrue={confirmActivate}
            titleKey="superadmin.settings.tabs.commitment-windows.bulkActivate.title"
          >
            <Typography sx={{ textAlign: 'center' }}>
              {t(
                'superadmin.settings.tabs.commitment-windows.bulkActivate.text',
                {
                  country: group.label,
                  start: startsOn,
                  end: endsOn,
                }
              )}
            </Typography>
          </ConfirmationModal>
        </>
      )}
    </TableRow>
  );
}

interface RowProps {
  row: CommitmentWindowRowData;
  isSaving: boolean;
  isClearing: boolean;
  onSave: (_values: {
    starts_on: string;
    ends_on: string;
    active: boolean;
  }) => void;
  onClear: () => void;
}

// BR-2: `row.can_edit` decides between two entirely different cell sets --
// a read-only admin sees the SAME values (dates/active/state) as plain
// text, never a TextField/Switch/button, rather than merely disabled
// versions of the editable controls.
function CommitmentWindowRow(props: RowProps) {
  const { row, isSaving, isClearing, onSave, onClear } = props;
  const { t } = useTranslation();

  // Local edit buffer, re-synced whenever the underlying row changes (a
  // successful save/clear refetches and comes back through here) -- so an
  // in-flight edit on one field isn't lost by a refetch of the others, but
  // a completed mutation still reflects the server's own values afterward.
  const [startsOn, setStartsOn] = useState(row.starts_on || '');
  const [endsOn, setEndsOn] = useState(row.ends_on || '');
  const [active, setActive] = useState(row.active);

  useEffect(() => {
    setStartsOn(row.starts_on || '');
    setEndsOn(row.ends_on || '');
    setActive(row.active);
  }, [row.starts_on, row.ends_on, row.active]);

  const stateLabel = String(
    t(`superadmin.settings.tabs.commitment-windows.states.${row.state}`)
  );
  const canSave = Boolean(startsOn) && Boolean(endsOn) && !isSaving;

  if (!row.can_edit) {
    return (
      <TableRow>
        <TableCell>{row.branch_description}</TableCell>
        <TableCell>{row.starts_on || '-'}</TableCell>
        <TableCell>{row.ends_on || '-'}</TableCell>
        <TableCell align="center">
          {String(
            t(
              row.active
                ? 'superadmin.settings.tabs.commitment-windows.table.activeOn'
                : 'superadmin.settings.tabs.commitment-windows.table.activeOff'
            )
          )}
        </TableCell>
        <TableCell>{stateLabel}</TableCell>
        <TableCell />
      </TableRow>
    );
  }

  return (
    <TableRow>
      <TableCell>{row.branch_description}</TableCell>
      <TableCell>
        <TextField
          type="date"
          size="small"
          value={startsOn}
          onChange={(e) => setStartsOn(e.target.value)}
          inputProps={{
            'aria-label': `${t(
              'superadmin.settings.tabs.commitment-windows.table.startsOn'
            )} - ${row.branch_description}`,
          }}
        />
      </TableCell>
      <TableCell>
        <TextField
          type="date"
          size="small"
          value={endsOn}
          onChange={(e) => setEndsOn(e.target.value)}
          inputProps={{
            'aria-label': `${t(
              'superadmin.settings.tabs.commitment-windows.table.endsOn'
            )} - ${row.branch_description}`,
          }}
        />
      </TableCell>
      <TableCell align="center">
        <Switch
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
          aria-label={`${t(
            'superadmin.settings.tabs.commitment-windows.table.active'
          )} - ${row.branch_description}`}
        />
      </TableCell>
      <TableCell>{stateLabel}</TableCell>
      <TableCell align="center">
        <StyledFlex sx={{ justifyContent: 'center', gap: '4px' }}>
          <Tooltip
            title={String(
              t('superadmin.settings.tabs.commitment-windows.buttons.save')
            )}
          >
            <span>
              <IconButton
                size="small"
                disabled={!canSave}
                aria-label={`${t(
                  'superadmin.settings.tabs.commitment-windows.buttons.save'
                )} - ${row.branch_description}`}
                onClick={() =>
                  onSave({ starts_on: startsOn, ends_on: endsOn, active })
                }
              >
                {isSaving ? (
                  <CircularProgress size={16} />
                ) : (
                  <SaveSharpIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={String(
              t('superadmin.settings.tabs.commitment-windows.buttons.clear')
            )}
          >
            <span>
              <IconButton
                size="small"
                disabled={!row.starts_on || isClearing}
                aria-label={`${t(
                  'superadmin.settings.tabs.commitment-windows.buttons.clear'
                )} - ${row.branch_description}`}
                onClick={onClear}
              >
                {isClearing ? (
                  <CircularProgress size={16} />
                ) : (
                  <ClearSharpIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </StyledFlex>
      </TableCell>
    </TableRow>
  );
}

// eslint-disable-next-line no-unused-vars
export default function CommitmentWindows(props: ContentProps) {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.platform.user);
  const { branches: allBranches } = useBranchesWithCountry();
  const [countries, setCountries] = useState<string[]>([]);
  const [pendingBranch, setPendingBranch] = useState<string | null>(null);
  const [pendingGroup, setPendingGroup] = useState<string | null>(null);

  const { data, isLoading, isFetching, isError } = useQuery<
    CommitmentWindowRowData[]
  >('commitment-windows', () =>
    client.get<CommitmentWindowRowData[]>(WINDOWS_URL).then((res) => res.data)
  );
  const rows = useMemo(() => data || [], [data]);

  const { save, clearWindow, bulkSave } = useCommitmentWindowMutations();

  // Only used to resolve a code (e.g. "PL") to its translated display name
  // (e.g. "Polska") -- this endpoint's own rows don't carry a country name,
  // just the code (Interface/Contract). The actual scoped set of countries
  // this screen groups/filters by comes from `rows` itself, see
  // groupRowsByCountry's comment above.
  const countryNameByCode = useMemo(() => {
    const map = new Map<string, string>();
    allBranches.forEach((branch) => {
      if (branch.country_code && !map.has(branch.country_code)) {
        map.set(
          branch.country_code,
          branch.country_name || branch.country_code
        );
      }
    });
    return map;
  }, [allBranches]);

  const noCountryLabel = String(
    t('superadmin.settings.tabs.commitment-windows.table.noCountry')
  );

  const pickerOptions: CountryPickerOption[] = useMemo(() => {
    const seen = new Map<string, string>();
    let hasNoCountry = false;
    rows.forEach((row) => {
      if (row.country_code) {
        if (!seen.has(row.country_code)) {
          seen.set(
            row.country_code,
            countryNameByCode.get(row.country_code) || row.country_code
          );
        }
      } else {
        hasNoCountry = true;
      }
    });
    const opts: CountryPickerOption[] = Array.from(seen, ([code, name]) => ({
      code,
      name,
    }));
    if (hasNoCountry) {
      opts.push({
        code: NO_COUNTRY_VALUE,
        name: noCountryLabel,
        summaryLabel: noCountryLabel,
      });
    }
    return opts;
  }, [rows, countryNameByCode, noCountryLabel]);

  const filteredRows = useMemo(() => {
    if (countries.length === 0) return rows;
    return rows.filter((row) =>
      row.country_code
        ? countries.includes(row.country_code)
        : countries.includes(NO_COUNTRY_VALUE)
    );
  }, [rows, countries]);

  const groups = useMemo(
    () => groupRowsByCountry(filteredRows, countryNameByCode, noCountryLabel),
    [filteredRows, countryNameByCode, noCountryLabel]
  );

  // specs/001-raben-country-masteradmin/mass-specs/specs/
  // 20260826-fe-country-group-folding.md: every group starts collapsed on
  // first visit (Goal 3) -- `allCountryKeys` is UNFILTERED by the country
  // picker above so collapseAll()/expandAll() act on every country this
  // screen could ever show, matching StructureTable.tsx/OptimizedUsersTable
  // .tsx's identical convention.
  const allCountryKeys = useMemo(
    () => countryKeysOf(rows, NO_COUNTRY_VALUE),
    [rows]
  );
  const storageKey = `superadmin.countryFold.commitmentWindows.${
    user?.id ?? 'anon'
  }`;
  const collapse = useCountryGroupCollapse({
    storageKey,
    allKeys: allCountryKeys,
  });

  // EC-6 of the folding spec: a screen narrowed to exactly one group (the
  // country filter above) renders it expanded rather than collapsible into
  // uselessness -- see StructureTable.tsx's identical comment.
  const resolveOpen = (key: string) =>
    (groups.length === 1 && !collapse.isFullyCollapsed) || collapse.isOpen(key);

  const handleSave = (
    row: CommitmentWindowRowData,
    values: { starts_on: string; ends_on: string; active: boolean }
  ) => {
    setPendingBranch(row.branch);
    save.mutate({ branch: row.branch, ...values });
  };

  const handleClear = (row: CommitmentWindowRowData) => {
    setPendingBranch(row.branch);
    clearWindow.mutate(row.branch);
  };

  const handleGroupSaveDates = (
    group: CommitmentWindowGroup,
    values: { starts_on: string; ends_on: string }
  ) => {
    setPendingGroup(group.key);
    // Dates for everyone, activation untouched: a dated branch keeps its
    // own active flag, a branch getting its first window starts inactive
    // (the country switch is the lever that turns things on).
    bulkSave.mutate(
      group.rows
        .filter((row) => row.can_edit)
        .map((row) => ({
          branch: row.branch,
          starts_on: values.starts_on,
          ends_on: values.ends_on,
          active: row.starts_on && row.ends_on ? row.active : false,
        }))
    );
  };

  const handleGroupBulkActive = (
    group: CommitmentWindowGroup,
    active: boolean,
    dates: { starts_on: string; ends_on: string }
  ) => {
    setPendingGroup(group.key);
    const editableRows = group.rows.filter((row) => row.can_edit);
    if (active) {
      // ON (after the confirm): every branch gets the country row's dates
      // and turns on -- windowless branches included.
      bulkSave.mutate(
        editableRows.map((row) => ({
          branch: row.branch,
          starts_on: dates.starts_on,
          ends_on: dates.ends_on,
          active: true,
        }))
      );
    } else {
      // OFF: immediate, no dialog -- deactivate everything that has a
      // window, keeping each branch's own dates for a later re-enable.
      bulkSave.mutate(
        editableRows
          .filter((row) => row.starts_on && row.ends_on)
          .map((row) => ({
            branch: row.branch,
            starts_on: row.starts_on as string,
            ends_on: row.ends_on as string,
            active: false,
          }))
      );
    }
  };

  let content: React.ReactNode;
  if (isLoading) {
    content = <PageLoading />;
  } else if (isError) {
    content = <SomethingWentWrong />;
  } else if (rows.length === 0) {
    content = (
      <StyledPanel sx={{ mt: '24px' }}>
        <Typography align="center" color="text.secondary">
          {t('superadmin.settings.tabs.commitment-windows.states.empty')}
        </Typography>
      </StyledPanel>
    );
  } else {
    content = (
      <StyledPanel sx={{ mt: '24px', minWidth: '900px' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                {t('superadmin.settings.tabs.commitment-windows.table.branch')}
              </TableCell>
              <TableCell>
                {t(
                  'superadmin.settings.tabs.commitment-windows.table.startsOn'
                )}
              </TableCell>
              <TableCell>
                {t('superadmin.settings.tabs.commitment-windows.table.endsOn')}
              </TableCell>
              <TableCell align="center">
                {t('superadmin.settings.tabs.commitment-windows.table.active')}
              </TableCell>
              <TableCell>
                {t('superadmin.settings.tabs.commitment-windows.table.state')}
              </TableCell>
              <TableCell align="center">
                {t('superadmin.settings.tabs.commitment-windows.table.actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((group) => {
              const isOpen = resolveOpen(group.key);
              return (
                <React.Fragment key={group.key}>
                  <CommitmentWindowGroupRow
                    group={group}
                    isOpen={isOpen}
                    onToggleFold={() => collapse.toggle(group.key)}
                    foldActionLabel={String(
                      t(
                        isOpen
                          ? 'superadmin.settings.countryFolding.collapseGroup'
                          : 'superadmin.settings.countryFolding.expandGroup',
                        { name: group.label, count: group.rows.length }
                      )
                    )}
                    isSaving={bulkSave.isLoading && pendingGroup === group.key}
                    onSaveDates={(values) =>
                      handleGroupSaveDates(group, values)
                    }
                    onBulkActive={(active, dates) =>
                      handleGroupBulkActive(group, active, dates)
                    }
                  />
                  {isOpen &&
                    group.rows.map((row) => (
                      <CommitmentWindowRow
                        key={row.branch}
                        row={row}
                        isSaving={
                          save.isLoading && pendingBranch === row.branch
                        }
                        isClearing={
                          clearWindow.isLoading && pendingBranch === row.branch
                        }
                        onSave={(values) => handleSave(row, values)}
                        onClear={() => handleClear(row)}
                      />
                    ))}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </StyledPanel>
    );
  }

  return (
    <Box>
      <StyledFlex>
        <StyledTitle sx={{ fontSize: '28px' }}>
          {t('superadmin.settings.tabs.commitment-windows.title')}
        </StyledTitle>
      </StyledFlex>
      <br />
      <Typography variant="body1">
        {t('superadmin.settings.tabs.commitment-windows.subtitle')}
      </Typography>

      <StyledFlex
        sx={{
          justifyContent: 'flex-start',
          gap: '16px',
          mt: '16px',
          mb: '16px',
        }}
      >
        <CountryPicker
          options={pickerOptions}
          value={countries}
          onChange={setCountries}
          ariaLabel={String(
            t(
              'superadmin.settings.tabs.commitment-windows.filters.countryLabel'
            )
          )}
          allSelectedLabel={String(
            t(
              'superadmin.settings.tabs.commitment-windows.filters.allCountries'
            )
          )}
          searchPlaceholder={String(
            t('superadmin.settings.countryPicker.searchPlaceholder')
          )}
          noResultsText={String(
            t('superadmin.settings.countryPicker.noResults')
          )}
          selectedSummaryTemplate={String(
            t('superadmin.settings.countryPicker.selectedSummary')
          )}
          sx={{ minWidth: '180px' }}
        />
        <CountryGroupBulkControls
          collapseAllLabel={String(
            t('superadmin.settings.countryFolding.bulk.collapseAll')
          )}
          expandAllLabel={String(
            t('superadmin.settings.countryFolding.bulk.expandAll')
          )}
          onCollapseAll={collapse.collapseAll}
          onExpandAll={collapse.expandAll}
        />
        {isFetching && !isLoading && <CircularProgress size={20} />}
      </StyledFlex>

      {content}
    </Box>
  );
}
